import { useEffect, useRef } from "react";
import type { RescueConversation as Conversation } from "@io-spoke/contracts/rescueConversation";
import { useRescueConversation } from "../../hooks/useRescueConversation";
import { SlackConversationDelivery } from "./SlackConversationDelivery";
import { Button } from "../ui/button";

const choices = [
  { answer: "commitments", label: "Review commitments" },
  { answer: "customer_updates", label: "Review customer updates" },
  { answer: "both", label: "Review both" },
] as const;
const inactiveCopy = {
  cancelled: "Investigation cancelled. You can start again when you’re ready.",
  expired: "This question expired. Start again to review the linked sources.",
  stale: "The case changed. Start again to assess its linked sources.",
};

type Props = {
  caseId: string;
  evidenceIds: string[];
  onOpenEvidence: (id: string) => void;
  onReviewPlan: () => void;
};

function InvestigationResult({
  conversation,
  evidenceIds,
  onOpenEvidence,
  onReviewPlan,
}: Omit<Props, "caseId"> & { conversation: Conversation }) {
  const result = conversation.result;
  if (!result) return null;
  return (
    <div className="space-y-5">
      <p className="text-xs text-muted-foreground">
        {choices.find((choice) => choice.answer === result.focus)?.label} ·
        Assessed {new Date(result.assessedAt).toLocaleString()}
      </p>
      <p className="text-base leading-7">{result.summary}</p>
      <Button type="button" className="min-h-11" onClick={onReviewPlan}>
        Review next steps
      </Button>
      <div className="space-y-3">
        {result.findings.slice(0, 3).map((finding, index) => (
          <details
            key={`${finding.kind}:${index}`}
            className="border-l-2 border-orange-600/40 pl-4"
          >
            <summary className="min-h-11 cursor-pointer py-3 text-sm font-medium break-words focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              {finding.title}
            </summary>
            <p className="mt-1 text-sm leading-6 text-muted-foreground break-words">
              {finding.detail}
            </p>
          </details>
        ))}
      </div>
      {result.findings.length > 3 ? (
        <details>
          <summary className="min-h-11 cursor-pointer py-3 text-sm font-medium focus-visible:ring-2 focus-visible:ring-ring">
            More findings · {result.findings.length - 3}
          </summary>
          <div className="space-y-4">
            {result.findings.slice(3).map((finding, index) => (
              <div key={`${finding.kind}:${index}`}>
                <h3 className="font-medium break-words">{finding.title}</h3>
                <p className="mt-1 text-sm leading-6 text-muted-foreground break-words">
                  {finding.detail}
                </p>
              </div>
            ))}
          </div>
        </details>
      ) : null}
      {result.citations.length > 0 ? (
        <details className="space-y-3">
          <summary className="min-h-11 cursor-pointer py-3 text-sm font-medium focus-visible:ring-2 focus-visible:ring-ring">
            Linked source excerpts · {result.citations.length}
          </summary>
          {result.citations.map((citation, index) => (
            <figure
              key={`${citation.evidenceId}:${index}`}
              className="rounded-xl bg-muted/40 p-4"
            >
              <blockquote className="whitespace-pre-wrap break-words text-sm leading-6">
                {citation.quote}
              </blockquote>
              <figcaption className="mt-2 text-xs text-muted-foreground">
                {citation.sourceLabel}
              </figcaption>
              {evidenceIds.includes(citation.evidenceId) ? (
                <Button
                  type="button"
                  variant="link"
                  className="mt-1 min-h-11 max-w-full whitespace-normal px-0"
                  onClick={() => onOpenEvidence(citation.evidenceId)}
                >
                  Open source: {citation.sourceLabel}
                </Button>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">
                  Source is no longer linked to this case.
                </p>
              )}
            </figure>
          ))}
        </details>
      ) : null}
    </div>
  );
}

export function RescueConversation(props: Props) {
  return <ScopedRescueConversation key={props.caseId} {...props} />;
}

function ScopedRescueConversation(props: Props) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
    heading.current?.scrollIntoView?.({ block: "start", behavior: "auto" });
  }, []);
  const { conversation, error, pending, operation, loaded, run, refresh } =
    useRescueConversation(props.caseId);
  const inactive =
    conversation &&
    conversation.status !== "pending" &&
    conversation.status !== "completed";
  return (
    <section
      id="rescue-investigation-answer"
      aria-label="Case investigation"
      className="space-y-5 border-t px-5 py-6 sm:px-6"
    >
      <div>
        <p className="text-xs font-medium uppercase tracking-widest text-orange-800 dark:text-orange-300">
          IO Spoke
        </p>
        <h2
          ref={heading}
          tabIndex={-1}
          className="mt-2 scroll-mt-6 text-xl font-semibold focus-visible:outline-none"
        >
          Let’s look at this case.
        </h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          Review recorded commitments and customer updates from this case’s
          linked sources.
        </p>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {pending ? (
        <p role="status" className="text-sm text-muted-foreground">
          {operation === "load"
            ? "Loading your investigation…"
            : "Updating your investigation…"}
        </p>
      ) : null}
      {!loaded || error ? (
        <Button
          type="button"
          variant="outline"
          loading={pending}
          loadingLabel="Loading investigation…"
          onClick={() => void run("load")}
        >
          Reload investigation
        </Button>
      ) : null}
      {conversation?.status === "pending" ? (
        <div className="space-y-4">
          <p className="font-medium">{conversation.question}</p>
          <div className="flex flex-wrap gap-3">
            {choices.map((choice) => (
              <Button
                key={choice.answer}
                type="button"
                variant="outline"
                disabled={pending}
                loading={pending && operation === choice.answer}
                loadingLabel="Reviewing linked sources…"
                className="h-auto min-h-11 max-w-full whitespace-normal"
                onClick={() => void run(choice.answer)}
              >
                {choice.label}
              </Button>
            ))}
            <Button
              type="button"
              variant="ghost"
              disabled={pending}
              loading={pending && operation === "cancel"}
              loadingLabel="Cancelling…"
              onClick={() => void run("cancel")}
            >
              Cancel investigation
            </Button>
          </div>
        </div>
      ) : null}
      {conversation ? (
        <SlackConversationDelivery
          caseId={props.caseId}
          conversationId={conversation.id}
          conversationPending={conversation.status === "pending"}
          onRefreshConversation={refresh}
        />
      ) : null}
      {conversation?.status === "completed" ? (
        <InvestigationResult
          conversation={conversation}
          evidenceIds={props.evidenceIds}
          onOpenEvidence={props.onOpenEvidence}
          onReviewPlan={props.onReviewPlan}
        />
      ) : null}
      {conversation?.status === "completed" ? (
        <Button
          type="button"
          variant="ghost"
          disabled={pending}
          loading={pending && operation === "restart"}
          loadingLabel="Starting investigation…"
          onClick={() => void run("restart")}
        >
          Check another angle
        </Button>
      ) : null}
      {inactive ? (
        <div className="space-y-3">
          <p className="text-sm">
            {inactiveCopy[conversation.status as keyof typeof inactiveCopy]}
          </p>
          <Button
            type="button"
            variant="outline"
            loading={pending}
            loadingLabel="Starting investigation…"
            onClick={() => void run("restart")}
          >
            Start a new investigation
          </Button>
        </div>
      ) : null}
    </section>
  );
}
